import { useState, useMemo, useEffect, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import Card from "../../components/common/Card";
import Button from "../../components/common/Button";
import Input from "../../components/common/Input";
import Textarea from "../../components/common/Textarea";
import Select from "../../components/common/Select";
import Checkbox from "../../components/common/Checkbox";
import MultiSelect from "../../components/common/MultiSelect";
import SortableMultiSelect from "../../components/common/SortableMultiSelect";
import Alert from "../../components/common/Alert";
import Modal from "../../components/common/Modal";
import Table from "../../components/common/Table";
import ImageWithPlaceholder from "../../components/common/ImageWithPlaceholder";
import ActionButtons from "../../components/common/ActionButtons";
import Toast from "../../components/common/Toast";
import FilterBar from "../../components/reporting/FilterBar";
import { getFullImageUrl } from "../../utils/imageUrl";
import { useStationFeature } from "../../hooks/useStationFeature";
import {
  getStationErrorMessage,
  shouldRefreshStations,
} from "../../utils/stationErrorHandler";
import { getCatalogOwnershipMessage } from "../../utils/catalogOwnership";
import {
  useGetItemsQuery,
  useCreateItemMutation,
  useUpdateItemMutation,
  usePatchItemAvailabilityMutation,
  usePatchItemSortOrderMutation,
  useDeleteItemMutation,
  useGetCategoriesQuery,
  useGetCatalogReferenceQuery,
} from "../../store/api";

const initialFormState = {
  name: "",
  description: "",
  basePrice: "",
  categoryId: "",
  sku: "",
  stationCode: "",
  imageUrl: "",
  isAvailable: true,
  isTaxable: true,
  isVegetarian: false,
  isVegan: false,
  isSpicy: false,
  spiceLevel: 0,
  containsAlcohol: false,
  isHalal: false,
  isKosher: false,
};

const Items = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const categoryFilter = searchParams.get("category");
  const editParam = searchParams.get("edit");
  const addParam = searchParams.get("add");

  const {
    data: itemsData,
    isLoading: itemsLoading,
    isError: itemsError,
    error: itemsErrorData,
  } = useGetItemsQuery();
  const { data: categoriesData } = useGetCategoriesQuery();
  const {
    data: referenceData,
    isLoading: referenceLoading,
    isError: referenceError,
    error: referenceErrorData,
  } = useGetCatalogReferenceQuery();
  const [createItem, createState] = useCreateItemMutation();
  const [updateItem, updateState] = useUpdateItemMutation();
  const [patchItemAvailability] = usePatchItemAvailabilityMutation();
  const [patchItemSortOrder] = usePatchItemSortOrderMutation();
  const [deleteItem] = useDeleteItemMutation();
  const { stationFeatureActive, printTokensByStation, activeStations, refetchStations } =
    useStationFeature();

  const [form, setForm] = useState(initialFormState);
  const [editingId, setEditingId] = useState(null);
  const [initialEditValues, setInitialEditValues] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [removeImage, setRemoveImage] = useState(false);
  const [selectedProteinTypeIds, setSelectedProteinTypeIds] = useState([]);
  const [selectedAllergens, setSelectedAllergens] = useState([]);
  const [selectedModifierGroups, setSelectedModifierGroups] = useState([]);
  const [deleteModal, setDeleteModal] = useState({
    isOpen: false,
    itemId: null,
    itemName: "",
  });
  const [errorModal, setErrorModal] = useState({ isOpen: false, message: "" });
  const [toast, setToast] = useState(null);
  const [updatingAvailabilityId, setUpdatingAvailabilityId] = useState(null);
  const [pendingSortByItem, setPendingSortByItem] = useState({});
  const [savingSortByItem, setSavingSortByItem] = useState({});
  const [sortSuccessMessage, setSortSuccessMessage] = useState("");
  const [searchQuery, setSearchQuery] = useState("");

  // Track if we came from categories page
  const cameFromCategories = useRef(false);
  const sortSuccessTimeoutRef = useRef(null);

  useEffect(() => {
    return () => {
      if (sortSuccessTimeoutRef.current) {
        clearTimeout(sortSuccessTimeoutRef.current);
      }
    };
  }, []);

  const items = Array.isArray(itemsData) ? itemsData : [];
  const categories = Array.isArray(categoriesData) ? categoriesData : [];
  const allergens = Array.isArray(referenceData?.allergens)
    ? referenceData.allergens
    : [];
  const proteinTypes = Array.isArray(referenceData?.proteinTypes)
    ? referenceData.proteinTypes
    : [];
  const modifierGroups = Array.isArray(referenceData?.modifierGroups)
    ? referenceData.modifierGroups
    : [];
  const modifierOptions = Array.isArray(referenceData?.modifierOptions)
    ? referenceData.modifierOptions
    : [];

  // Handle edit from URL parameter
  useEffect(() => {
    // Deprecated in Edge: full item edit flow.
    if (false && editParam && !showForm && items.length > 0) {
      const itemToEdit = items.find((i) => i.id === editParam);
      if (itemToEdit) {
        handleEdit(itemToEdit);
      }
    }
  }, [editParam]);

  // Handle add with category pre-selected
  useEffect(() => {
    // Deprecated in Edge: full item create flow.
    if (false && addParam === "true" && categoryFilter && !showForm) {
      cameFromCategories.current = true; // Mark that we came from categories
      setForm((prev) => ({
        ...prev,
        categoryId: categoryFilter,
      }));
      setShowForm(true);
      // Clear the add parameter from URL
      setSearchParams({ category: categoryFilter });
    }
  }, [addParam, categoryFilter]);

  // Filter items by category if filter is active
  const filteredItemsByCategory = useMemo(() => {
    if (!categoryFilter) return items;
    return items.filter((item) => item.categoryId === categoryFilter);
  }, [items, categoryFilter]);

  const filteredItems = useMemo(() => {
    const query = searchQuery.trim().toLowerCase();
    const base = !query
      ? filteredItemsByCategory
      : filteredItemsByCategory.filter((item) => {
      const haystack = `${item.name || ""} ${item.description || ""}`
        .trim()
        .toLowerCase();
      return haystack.includes(query);
    });
    const categoryOrder = new Map(
      [...categories]
        .sort(
          (a, b) =>
            (Number(a.sort ?? 0) - Number(b.sort ?? 0)) ||
            (a.name || "").localeCompare(b.name || ""),
        )
        .map((category, index) => [category.id, index]),
    );
    return [...base].sort((a, b) => {
      const categorySortDelta =
        (categoryOrder.get(a.categoryId) ?? Number.MAX_SAFE_INTEGER) -
        (categoryOrder.get(b.categoryId) ?? Number.MAX_SAFE_INTEGER);
      if (categorySortDelta !== 0) return categorySortDelta;
      return (
        (Number(a.sort ?? 0) - Number(b.sort ?? 0)) ||
        (a.name || "").localeCompare(b.name || "")
      );
    });
  }, [filteredItemsByCategory, searchQuery, categories]);

  // Get filtered category info for breadcrumb
  const filteredCategory = useMemo(() => {
    if (!categoryFilter) return null;
    return categories.find((cat) => cat.id === categoryFilter);
  }, [categoryFilter, categories]);

  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: "auto" });
  }, [categoryFilter]);

  const handleCategoryFilterChange = (e) => {
    const value = e.target.value;
    if (value) {
      setSearchParams({ category: value });
    } else {
      setSearchParams({});
    }
  };

  const clearFilter = () => {
    setSearchParams({});
    setSearchQuery("");
  };

  const categorySelectNode = (
    <div key="category" className="min-w-[180px] max-w-[260px]">
      <label className="mb-1 block text-xs font-medium text-slate-500 dark:text-slate-400">
        Category
      </label>
      <select
        value={categoryFilter || ""}
        onChange={handleCategoryFilterChange}
        className="h-9 w-full rounded border border-slate-200 bg-white px-3 text-sm text-slate-900 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-100"
      >
        <option value="">All Categories</option>
        {categories
          .filter((cat) => cat.isActive)
          .sort((a, b) => a.sort - b.sort)
          .map((cat) => (
            <option key={cat.id} value={cat.id}>
              {cat.name}
            </option>
          ))}
      </select>
    </div>
  );

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
    if (name === "imageUrl" && !imageFile) {
      const resolved = value ? getFullImageUrl(value) : null;
      setImagePreview(resolved);
      setRemoveImage(false);
    }
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (!file.type.match(/image\/(png|jpg|jpeg)/)) {
        setErrorModal({
          isOpen: true,
          message: "Only PNG and JPG images are allowed",
        });
        return;
      }
      if (file.size > 2 * 1024 * 1024) {
        setErrorModal({
          isOpen: true,
          message: "File size must be less than 2MB",
        });
        return;
      }
      setImageFile(file);
      setRemoveImage(false);
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingId) {
        const payload = {
          name: form.name,
          description: form.description,
          basePrice: parseFloat(form.basePrice),
          categoryId: form.categoryId,
          sku: form.sku,
          stationCode: stationFeatureActive ? form.stationCode || null : undefined,
          imageUrl: form.imageUrl?.trim() || "",
          spiceLevel: form.isSpicy ? parseInt(form.spiceLevel) || 0 : null,
          proteinTypeIdsJson: JSON.stringify(selectedProteinTypeIds || []),
          allergensJson: JSON.stringify(
            (selectedAllergens || []).map((allergen) => ({
              allergenId: allergen.allergenId,
              note: allergen.note || "",
            })),
          ),
          modifierGroupsJson: JSON.stringify(
            (selectedModifierGroups || []).map((group) => ({
              groupId: group.groupId,
              sort: group.sort || 1,
            })),
          ),
        };
        const booleanFields = [
          "isAvailable",
          "isTaxable",
          "isVegetarian",
          "isVegan",
          "isSpicy",
          "containsAlcohol",
          "isHalal",
          "isKosher",
        ];
        booleanFields.forEach((field) => {
          if (!initialEditValues || form[field] !== initialEditValues[field]) {
            payload[field] = form[field];
          }
        });
        await updateItem({
          itemId: editingId,
          payload,
          file: imageFile,
          removeImage,
        }).unwrap();
        setEditingId(null);
        setInitialEditValues(null);
      } else {
        const payload = {
          ...form,
          stationCode: stationFeatureActive ? form.stationCode || null : undefined,
          basePrice: parseFloat(form.basePrice),
          spiceLevel: form.isSpicy ? parseInt(form.spiceLevel) || 0 : null,
          imageUrl: form.imageUrl?.trim() || "",
          proteinTypeIdsJson: JSON.stringify(selectedProteinTypeIds || []),
          allergensJson: JSON.stringify(
            (selectedAllergens || []).map((allergen) => ({
              allergenId: allergen.allergenId,
              note: allergen.note || "",
            })),
          ),
          modifierGroupsJson: JSON.stringify(
            (selectedModifierGroups || []).map((group) => ({
              groupId: group.groupId,
              sort: group.sort || 1,
            })),
          ),
        };
        await createItem({
          payload,
          file: imageFile,
        }).unwrap();
      }
      setForm(initialFormState);
      setShowForm(false);
      setImageFile(null);
      setImagePreview(null);
      setRemoveImage(false);
      setSelectedProteinTypeIds([]);
      setSelectedAllergens([]);
      setSelectedModifierGroups([]);
    } catch (err) {
      if (shouldRefreshStations(err)) {
        refetchStations();
      }
    }
  };

  const handleEdit = (item) => {
    setEditingId(item.id);
    setForm({
      name: item.name || "",
      description: item.description || "",
      basePrice: item.basePrice || "",
      categoryId: item.categoryId || "",
      sku: item.sku || "",
      stationCode: item.stationCode || "",
      imageUrl: item.imageUrl || "",
      isAvailable: item.isAvailable !== undefined ? item.isAvailable : true,
      isTaxable: item.isTaxable !== undefined ? item.isTaxable : true,
      isVegetarian: item.isVegetarian || false,
      isVegan: item.isVegan || false,
      isSpicy: item.isSpicy || false,
      spiceLevel: item.spiceLevel || 0,
      containsAlcohol: item.containsAlcohol || false,
      isHalal: item.isHalal || false,
      isKosher: item.isKosher || false,
    });
    setInitialEditValues({
      isAvailable: item.isAvailable !== undefined ? item.isAvailable : true,
      isTaxable: item.isTaxable !== undefined ? item.isTaxable : true,
      isVegetarian: item.isVegetarian || false,
      isVegan: item.isVegan || false,
      isSpicy: item.isSpicy || false,
      containsAlcohol: item.containsAlcohol || false,
      isHalal: item.isHalal || false,
      isKosher: item.isKosher || false,
    });
    setImagePreview(getFullImageUrl(item.imageUrl));
    setImageFile(null);
    setRemoveImage(false);
    setSelectedProteinTypeIds(item.proteinTypeIds || []);
    setSelectedAllergens(
      (item.allergens || []).map((allergen) => ({
        allergenId: allergen.allergenId || allergen.id,
        note: allergen.note || "",
      })),
    );
    setSelectedModifierGroups(
      (item.modifierGroups || []).map((group, index) => ({
        groupId: group.groupId || group.id,
        sort: group.sort ?? index + 1,
      })),
    );
    setShowForm(true);
  };

  const handleDelete = async (itemId) => {
    const item = items.find((i) => i.id === itemId);
    setDeleteModal({
      isOpen: true,
      itemId,
      itemName: item?.name || "this item",
    });
  };

  const handleToggleAvailability = async (item) => {
    setUpdatingAvailabilityId(item.id);
    try {
      await patchItemAvailability({
        itemId: item.id,
        isAvailable: !item.isAvailable,
      }).unwrap();
    } catch (err) {
      setErrorModal({
        isOpen: true,
        message: getCatalogOwnershipMessage(
          err,
          "Failed to update item availability.",
        ),
      });
    } finally {
      setUpdatingAvailabilityId(null);
    }
  };

  const getItemSortInputValue = (item) => {
    if (Object.prototype.hasOwnProperty.call(pendingSortByItem, item.id)) {
      return pendingSortByItem[item.id];
    }
    return String(Number(item.sort ?? 0));
  };

  const handleItemSortInputChange = (itemId, value) => {
    if (!/^\d*$/.test(value)) return;
    setPendingSortByItem((prev) => ({
      ...prev,
      [itemId]: value,
    }));
  };

  const handleSaveItemSort = async (item) => {
    const rawValue = getItemSortInputValue(item);
    if (rawValue === "") {
      setErrorModal({ isOpen: true, message: "Sort order must be >= 0." });
      return;
    }
    const sortOrder = Number(rawValue);
    if (!Number.isInteger(sortOrder) || sortOrder < 0) {
      setErrorModal({ isOpen: true, message: "Sort order must be >= 0." });
      return;
    }
    if (sortOrder === Number(item.sort ?? 0)) return;
    setSavingSortByItem((prev) => ({ ...prev, [item.id]: true }));
    try {
      await patchItemSortOrder({ itemId: item.id, sortOrder }).unwrap();
      setPendingSortByItem((prev) => {
        const next = { ...prev };
        delete next[item.id];
        return next;
      });
      setToast({
        id: Date.now(),
        tone: "success",
        message: `Display order saved for ${item.name}.`,
      });
    } catch (err) {
      const fallbackMessage =
        err?.data?.status === 409
          ? "This sort order is already used in this category."
          : "Sort order is already assigned. Choose another number.";
      setErrorModal({
        isOpen: true,
        message: err?.data?.detail || fallbackMessage,
      });
    } finally {
      setSavingSortByItem((prev) => ({ ...prev, [item.id]: false }));
    }
  };

  const confirmDelete = async () => {
    try {
      await deleteItem(deleteModal.itemId).unwrap();
      setDeleteModal({ isOpen: false, itemId: null, itemName: "" });
    } catch (err) {
      setDeleteModal({ isOpen: false, itemId: null, itemName: "" });
      const errorMessage =
        err?.data?.detail || err?.data?.message || "Failed to delete item.";
      setErrorModal({ isOpen: true, message: errorMessage });
    }
  };

  const handleCancel = () => {
    setEditingId(null);
    setForm(initialFormState);
    setShowForm(false);
    setImageFile(null);
    setImagePreview(null);
    setRemoveImage(false);
    setInitialEditValues(null);
    setSelectedProteinTypeIds([]);
    setSelectedAllergens([]);
    setSelectedModifierGroups([]);

    // If we came from categories with add=true, go back to categories
    if (cameFromCategories.current) {
      cameFromCategories.current = false; // Reset the flag
      navigate("/menu/categories");
    } else if (editParam) {
      // Clear edit parameter from URL
      setSearchParams(categoryFilter ? { category: categoryFilter } : {});
    }
  };

  const columns = [
    {
      key: "name",
      label: "Item",
      headerClassName: "py-1 text-[11px]",
      cellClassName: "py-0.5 text-xs",
    },
    {
      key: "category",
      label: "Category",
      headerClassName: "py-1 text-[11px]",
      cellClassName: "py-0.5 text-xs",
    },
    {
      key: "price",
      label: "Price",
      headerClassName: "py-1 text-[11px]",
      cellClassName: "py-0.5 text-xs",
    },
    {
      key: "status",
      label: "Status",
      headerClassName: "py-1 text-[11px]",
      cellClassName: "py-0.5 text-xs",
    },
    {
      key: "sort",
      label: "Display Order",
      headerClassName: "py-1 text-[11px]",
      cellClassName: "py-0.5 text-xs",
    },
    {
      key: "actions",
      label: "Actions",
      headerClassName: "py-1 text-[11px]",
      cellClassName: "py-0.5 text-xs",
    },
  ];

  const rows = filteredItems.map((item) => {
    const category = categories.find((c) => c.id === item.categoryId);

    return {
      id: item.id,
      name: (
        <div className="flex items-start gap-3">
          <div className="mt-0.5 shrink-0">
            <ImageWithPlaceholder src={item.imageUrl} alt={item.name} size="xs" />
          </div>
          <div className="min-w-0 flex-1 space-y-1">
            <div className="text-sm font-semibold leading-tight text-slate-900 dark:text-slate-100">
              {item.name}
            </div>
            {item.description && (
              <div
                className="overflow-hidden rounded-lg bg-slate-50 px-2.5 py-1.5 text-xs leading-5 text-slate-600 dark:bg-slate-800/60 dark:text-slate-300"
                style={{
                  display: "-webkit-box",
                  WebkitBoxOrient: "vertical",
                  WebkitLineClamp: 2,
                }}
                title={item.description}
              >
                {item.description}
              </div>
            )}
          </div>
        </div>
      ),
      category: category ? (
        <span className="text-xs text-slate-700 dark:text-slate-300">
          {category.name}
        </span>
      ) : (
        <span className="text-xs text-slate-400 dark:text-slate-500">—</span>
      ),
      price: (
        <div className="text-xs">
          <div className="font-semibold text-slate-900 dark:text-slate-100">
            ${parseFloat(item.basePrice || 0).toFixed(2)}
          </div>
        </div>
      ),
      status: (
        <div className="flex items-center gap-2">
          <input
            type="checkbox"
            checked={Boolean(item.isAvailable)}
            onChange={() => handleToggleAvailability(item)}
            disabled={updatingAvailabilityId === item.id}
            className="h-4 w-4 cursor-pointer rounded border-slate-300 text-indigo-600 focus:ring-indigo-500 dark:border-slate-600 dark:bg-slate-800"
            aria-label={
              item.isAvailable
                ? `Mark ${item.name} unavailable`
                : `Mark ${item.name} available`
            }
          />
          <span className="text-xs text-slate-700 dark:text-slate-300">
            {item.isAvailable ? "Available" : "Unavailable"}
          </span>
        </div>
      ),
      sort: (
        <div className="flex items-center gap-2">
          <input
            type="number"
            min="0"
            value={getItemSortInputValue(item)}
            onChange={(e) => handleItemSortInputChange(item.id, e.target.value)}
            className="h-8 w-20 rounded-md border border-slate-300 px-2 text-xs text-slate-900 focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500 dark:border-slate-600 dark:bg-slate-800 dark:text-slate-100"
            aria-label={`Sort order for ${item.name}`}
          />
          <Button
            size="sm"
            variant="secondary"
            onClick={() => handleSaveItemSort(item)}
            disabled={Boolean(savingSortByItem[item.id])}
          >
            Save
          </Button>
        </div>
      ),
      actions: (
        <ActionButtons
          actions={[
            {
              icon: "view",
              label: "View",
              color: "secondary",
              onClick: () => navigate(`/menu/items/${item.id}`),
            },
            // Deprecated in Edge: item edit/delete actions.
            // {
            //   icon: "edit",
            //   label: "Edit",
            //   color: "primary",
            //   onClick: () => handleEdit(item),
            // },
            // {
            //   icon: "delete",
            //   label: "Delete",
            //   color: "danger",
            //   onClick: () => handleDelete(item.id),
            // },
          ]}
        />
      ),
    };
  });

  return (
    <div className="space-y-3">
      {toast && (
        <Toast
          message={toast.message}
          tone={toast.tone}
          onClose={() => setToast(null)}
        />
      )}

      {!showForm && (
        <div className="space-y-3 px-1">
          <div className="flex items-center gap-2">
            <h1 className="text-2xl font-semibold tracking-tight text-slate-800 dark:text-slate-100">
              Items
            </h1>
            {categoryFilter && (
              <Button
                size="sm"
                variant="secondary"
                onClick={() => navigate("/menu/categories")}
              >
                Back
              </Button>
            )}
          </div>
          <FilterBar
            fieldOrder={["search", "category"]}
            showChannel={false}
            showSearch
            search={searchQuery}
            onSearchChange={setSearchQuery}
            searchPlaceholder="Search items..."
            searchFieldClassName="flex-[0_0_360px] max-w-[360px]"
            customFields={[{ key: "category", node: categorySelectNode }]}
            onReset={clearFilter}
          />
        </div>
      )}

      {!showForm && (
        <Card className="p-0 border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-800 shadow-sm rounded-xl overflow-hidden">
          <div className="px-2 pt-0 pb-2 md:px-2.5">
            {itemsLoading && (
              <p className="text-sm text-slate-500 dark:text-slate-400">
                Loading items...
              </p>
            )}
            {itemsError && (
              <Alert tone="error">
                {getCatalogOwnershipMessage(
                  itemsErrorData,
                  "Failed to load items. Catalog is managed in Central Admin.",
                )}
              </Alert>
            )}
            {sortSuccessMessage && (
              <div className="fixed bottom-4 right-4 z-50 w-[min(92vw,28rem)] shadow-xl">
                <Alert
                  tone="success"
                  className="border-emerald-200 bg-emerald-50/95 backdrop-blur"
                  onClose={() => {
                    if (sortSuccessTimeoutRef.current) {
                      clearTimeout(sortSuccessTimeoutRef.current);
                      sortSuccessTimeoutRef.current = null;
                    }
                    setSortSuccessMessage("");
                  }}
                >
                  {sortSuccessMessage}
                </Alert>
              </div>
            )}
            {!itemsLoading && !itemsError && (
              <Table
                columns={columns}
                rows={rows}
                emptyText="No items synced yet. Create items in Central Admin and wait for Edge sync."
              />
            )}
          </div>
        </Card>
      )}

      {/* Deprecated in Edge: full item create/edit form */}
      {false && showForm && (
        <Card>
          <h2 className="mb-4 text-lg font-semibold text-slate-900 dark:text-slate-100">
            {editingId ? "Edit Item" : "Add Item"}
          </h2>

          {/* Category Info Alert - Show when adding from categories */}
          {!editingId && cameFromCategories.current && filteredCategory && (
            <Alert tone="info" className="mb-4">
              Adding item to <strong>{filteredCategory.name}</strong> category
            </Alert>
          )}

          <form className="space-y-4" onSubmit={handleSubmit}>
            {/* Image Upload Section */}
            <div className="rounded-lg border border-slate-200 dark:border-slate-700 p-4">
              <label className="mb-2 block text-sm font-medium text-slate-700 dark:text-slate-300">
                Item Image
              </label>
              <div className="flex items-start gap-4">
                <div className="flex-shrink-0">
                  {imagePreview ? (
                    <img
                      src={imagePreview}
                      alt="Item preview"
                      className="h-24 w-24 rounded-lg border border-slate-200 dark:border-slate-700 object-cover"
                    />
                  ) : (
                    <div className="flex h-24 w-24 items-center justify-center rounded-lg border-2 border-dashed border-slate-300 dark:border-slate-600 bg-slate-50 dark:bg-slate-800">
                      <svg
                        className="h-8 w-8 text-slate-400 dark:text-slate-500"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"
                        />
                      </svg>
                    </div>
                  )}
                </div>
                <div className="flex-1">
                  <p className="mb-2 text-xs text-slate-600 dark:text-slate-400">
                    PNG or JPG, max 2MB, max 1024x1024px
                  </p>
                  <div className="flex gap-2">
                    <label className="cursor-pointer">
                      <input
                        type="file"
                        accept="image/png,image/jpeg,image/jpg"
                        onChange={handleImageChange}
                        className="hidden"
                      />
                      <span className="inline-flex h-9 items-center rounded-lg border border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-700 px-3 text-sm font-semibold text-slate-700 dark:text-slate-300 transition hover:border-slate-300 dark:hover:border-slate-500">
                        Choose File
                      </span>
                    </label>
                    {imagePreview && !imageFile && editingId && (
                      <Button
                        type="button"
                        size="sm"
                        variant="secondary"
                        onClick={() => {
                          setImagePreview(null);
                          setRemoveImage(true);
                        }}
                      >
                        Remove
                      </Button>
                    )}
                  </div>
                  <div className="mt-3">
                    <Input
                      label="Image URL (Optional)"
                      name="imageUrl"
                      placeholder="/images/items/example.png"
                      value={form.imageUrl}
                      onChange={handleChange}
                      disabled={!!imageFile}
                      helper={
                        imageFile
                          ? "Remove the file to use a URL."
                          : "Paste a full or relative URL."
                      }
                    />
                  </div>
                </div>
              </div>
            </div>

            <div className="grid gap-4 md:grid-cols-2">
              <Input
                label="Item Name"
                name="name"
                placeholder="Margherita Pizza"
                value={form.name}
                onChange={handleChange}
                required
              />
              <Select
                label="Category"
                name="categoryId"
                value={form.categoryId}
                onChange={handleChange}
                required
                disabled={!editingId && cameFromCategories.current}
              >
                <option value="">Select a category</option>
                {categories
                  .filter((cat) => cat.isActive)
                  .sort((a, b) => a.sort - b.sort)
                  .map((cat) => (
                    <option key={cat.id} value={cat.id}>
                      {cat.name}
                    </option>
                  ))}
              </Select>
            </div>

            {stationFeatureActive && (
              <Select
                label="Station Override"
                name="stationCode"
                value={form.stationCode}
                onChange={handleChange}
                helper="Leave empty to use category default station."
              >
                <option value="">Use Category Default</option>
                {activeStations
                  .sort((a, b) => (a.sort ?? 0) - (b.sort ?? 0))
                  .map((station) => (
                    <option key={station.stationId} value={station.stationCode}>
                      {station.displayName} ({station.stationCode})
                    </option>
                  ))}
              </Select>
            )}

            {printTokensByStation && activeStations.length === 0 && (
              <Alert tone="warning">
                Print Tokens By Station is enabled, but there are no active
                stations. Create/activate a station to enable station override.
              </Alert>
            )}

            <Textarea
              label="Description"
              name="description"
              placeholder="Fresh mozzarella, tomato sauce, and basil"
              value={form.description}
              onChange={handleChange}
            />

            <div className="grid gap-4 md:grid-cols-3">
              <Input
                label="Base Price"
                name="basePrice"
                type="number"
                step="0.01"
                placeholder="12.99"
                value={form.basePrice}
                onChange={handleChange}
                required
              />
              <Input
                label="SKU (Optional)"
                name="sku"
                placeholder="PIZZA-001"
                value={form.sku}
                onChange={handleChange}
              />
              <div className="flex flex-col gap-2">
                <span className="text-sm text-slate-600 dark:text-slate-400">
                  Options
                </span>
                <div className="flex gap-4 pt-2">
                  <Checkbox
                    label="Available"
                    name="isAvailable"
                    checked={form.isAvailable}
                    onChange={handleChange}
                  />
                  <Checkbox
                    label="Taxable"
                    name="isTaxable"
                    checked={form.isTaxable}
                    onChange={handleChange}
                  />
                </div>
              </div>
            </div>

            <div className="rounded-lg border border-slate-200 dark:border-slate-700 p-4">
              <h3 className="mb-3 text-sm font-semibold text-slate-700 dark:text-slate-300">
                Dietary Information
              </h3>
              <div className="grid gap-3 md:grid-cols-3">
                <Checkbox
                  label="Vegetarian"
                  name="isVegetarian"
                  checked={form.isVegetarian}
                  onChange={handleChange}
                />
                <Checkbox
                  label="Vegan"
                  name="isVegan"
                  checked={form.isVegan}
                  onChange={handleChange}
                />
                <Checkbox
                  label="Halal"
                  name="isHalal"
                  checked={form.isHalal}
                  onChange={handleChange}
                />
                <Checkbox
                  label="Kosher"
                  name="isKosher"
                  checked={form.isKosher}
                  onChange={handleChange}
                />
                <Checkbox
                  label="Contains Alcohol"
                  name="containsAlcohol"
                  checked={form.containsAlcohol}
                  onChange={handleChange}
                />
                <Checkbox
                  label="Spicy"
                  name="isSpicy"
                  checked={form.isSpicy}
                  onChange={handleChange}
                />
              </div>
              {form.isSpicy && (
                <div className="mt-3">
                  <Input
                    label="Spice Level (0-5)"
                    name="spiceLevel"
                    type="number"
                    min="0"
                    max="5"
                    value={form.spiceLevel}
                    onChange={handleChange}
                    helper="0 = Mild, 5 = Very Hot"
                  />
                </div>
              )}
            </div>

            <div className="rounded-lg border border-slate-200 dark:border-slate-700 p-4">
              <h3 className="mb-3 text-sm font-semibold text-slate-700 dark:text-slate-300">
                Item Metadata
              </h3>
              {referenceLoading && (
                <p className="text-sm text-slate-500 dark:text-slate-400">
                  Loading reference data...
                </p>
              )}
              {referenceError && (
                <Alert tone="error">
                  {referenceErrorData?.data?.detail ||
                    "Failed to load reference data."}
                </Alert>
              )}
              {!referenceLoading && !referenceError && (
                <div className="space-y-6">
                  <MultiSelect
                    label="Protein Types"
                    options={proteinTypes
                      .filter((type) => type.isActive !== false)
                      .sort((a, b) => (a.sort ?? 0) - (b.sort ?? 0))}
                    selected={selectedProteinTypeIds}
                    onChange={setSelectedProteinTypeIds}
                    helper="Select protein classifications for this item."
                  />

                  <div className="space-y-3">
                    <MultiSelect
                      label="Allergens"
                      options={allergens
                        .filter((allergen) => allergen.isActive !== false)
                        .sort((a, b) => (a.sort ?? 0) - (b.sort ?? 0))}
                      selected={selectedAllergens.map(
                        (allergen) => allergen.allergenId,
                      )}
                      onChange={(selectedIds) => {
                        setSelectedAllergens((prev) => {
                          const prevMap = new Map(
                            prev.map((entry) => [
                              entry.allergenId,
                              entry.note || "",
                            ]),
                          );
                          return selectedIds.map((id) => ({
                            allergenId: id,
                            note: prevMap.get(id) || "",
                          }));
                        });
                      }}
                      helper="Add optional notes for each selected allergen."
                    />
                    {selectedAllergens.length > 0 && (
                      <div className="grid gap-3 md:grid-cols-2">
                        {selectedAllergens.map((allergen) => {
                          const allergenInfo = allergens.find(
                            (entry) => entry.id === allergen.allergenId,
                          );
                          return (
                            <Input
                              key={allergen.allergenId}
                              label={`Note - ${
                                allergenInfo?.name || "Allergen"
                              }`}
                              placeholder="Contains dairy"
                              value={allergen.note || ""}
                              onChange={(e) => {
                                const note = e.target.value;
                                setSelectedAllergens((prev) =>
                                  prev.map((entry) =>
                                    entry.allergenId === allergen.allergenId
                                      ? { ...entry, note }
                                      : entry,
                                  ),
                                );
                              }}
                            />
                          );
                        })}
                      </div>
                    )}
                  </div>

                  <SortableMultiSelect
                    label="Modifier Groups"
                    options={modifierGroups
                      .filter((group) => group.isActive !== false)
                      .sort((a, b) => (a.sort ?? 0) - (b.sort ?? 0))}
                    selected={selectedModifierGroups.map((group) => ({
                      itemId: group.groupId,
                      sortOrder: group.sort || 1,
                    }))}
                    onChange={(selected) => {
                      setSelectedModifierGroups(
                        selected.map((entry) => ({
                          groupId: entry.itemId,
                          sort: entry.sortOrder,
                        })),
                      );
                    }}
                    helper="Select and order modifier groups for this item."
                  />

                  {selectedModifierGroups.length > 0 && (
                    <div className="space-y-3">
                      <div className="text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">
                        Modifier Options (Preview)
                      </div>
                      <div className="grid gap-3 md:grid-cols-2">
                        {selectedModifierGroups.map((group) => {
                          const groupInfo = modifierGroups.find(
                            (entry) => entry.id === group.groupId,
                          );
                          const options = modifierOptions
                            .filter(
                              (option) => option.groupId === group.groupId,
                            )
                            .sort((a, b) => (a.sort ?? 0) - (b.sort ?? 0));
                          return (
                            <div
                              key={group.groupId}
                              className="rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 p-3"
                            >
                              <div className="text-sm font-semibold text-slate-700 dark:text-slate-200">
                                {groupInfo?.name || "Modifier Group"}
                              </div>
                              <div className="mt-2 space-y-1 text-xs text-slate-600 dark:text-slate-400">
                                {options.length === 0 && (
                                  <div>No options configured.</div>
                                )}
                                {options.map((option) => (
                                  <div
                                    key={option.id}
                                    className="flex items-center justify-between"
                                  >
                                    <span>{option.name}</span>
                                    <span className="text-slate-500">
                                      {option.priceDelta
                                        ? `+${option.priceDelta}`
                                        : "+0.00"}
                                    </span>
                                  </div>
                                ))}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>

            {(createState.isSuccess || updateState.isSuccess) && (
              <Alert tone="success">
                Item {editingId ? "updated" : "created"} successfully.
              </Alert>
            )}
            {(createState.isError || updateState.isError) && (
              <Alert tone="error">
                {getStationErrorMessage(
                  createState.error || updateState.error,
                  "Failed to save item.",
                )}
              </Alert>
            )}

            <div className="flex gap-3">
              <Button
                type="submit"
                disabled={createState.isLoading || updateState.isLoading}
              >
                {editingId ? "Update" : "Add"} Item
              </Button>
              <Button type="button" variant="secondary" onClick={handleCancel}>
                Cancel
              </Button>
            </div>
          </form>
        </Card>
      )}

      {/* Deprecated in Edge: item delete modal */}
      {false && (
        <Modal
          isOpen={deleteModal.isOpen}
          onClose={() =>
            setDeleteModal({ isOpen: false, itemId: null, itemName: "" })
          }
          title="Delete Item"
          confirmText="Delete"
          confirmTone="danger"
          onConfirm={confirmDelete}
        >
          <p>
            Are you sure you want to delete{" "}
            <strong>{deleteModal.itemName}</strong>?
          </p>
          <p className="mt-2 text-sm text-slate-600 dark:text-slate-400">
            This action cannot be undone.
          </p>
        </Modal>
      )}

      {/* Error Modal */}
      <Modal
        isOpen={errorModal.isOpen}
        onClose={() => setErrorModal({ isOpen: false, message: "" })}
        title="Error"
        confirmText="OK"
        onConfirm={() => setErrorModal({ isOpen: false, message: "" })}
      >
        <p>{errorModal.message}</p>
      </Modal>
    </div>
  );
};

export default Items;

